import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import Group from '../../../../models/Group';
import ReadOnlyField from './../ReadOnly';
import { IGroupsService } from '../../../../services/IGroupsService';
import GroupsService from '../../../../services/GroupsService';
import { LoadingIndicator, Typeahead, TypeaheadItem } from '@emorg-prd/standard-react';
import { useValidation, FieldProps } from '../ValidationContext';

interface GroupLookupProps extends FieldProps {
	label: string;
	placeholder?: string;
	note?: string;
	readonly?: boolean;
	groupsService?: IGroupsService;
	disabled?: boolean;
	onChange?: (group: any | null) => void;
}

const GroupLookup = (props: GroupLookupProps) => {
	const groupService: IGroupsService = props.groupsService || new GroupsService();
	const [results, setResults] = useState<Group[]>([]);
	const [groupText, setGroupText] = useState<string>('');
	const [loading, setLoading] = useState<boolean>(false);
	const prevValueRef = useRef(props.value);
	const { isValid, nonValidMessage } = useValidation(props.value, props.validation);

	useEffect(() => {
		resetInputText(props.value);
	}, []);

	useEffect(() => {
		if (prevValueRef.current !== props.value) {
			resetInputText(props.value);
			prevValueRef.current = props.value;
		}
	}, [props.value]);

	const resetInputText = (newValue) => {
		let groupText = '';
		if (typeof newValue === 'string') {
			groupText = newValue;
		} else if (newValue && typeof newValue === 'object') {
			groupText = newValue.displayName ?? '';
		}
		setGroupText(groupText);
		setResults([]);
	};

	const handleTextChange = async (e) => {
		const inputText = e.target.value;
		resetInputText(inputText);
		props.onChange(null);
		const searchText = inputText.trim();
		if (searchText.length >= 3) {
			try {
				setLoading(true);
				const response = await groupService.groupLookup(searchText);
				setResults(response);
				setLoading(false);
			} catch (error) {
				setResults([]);
				setLoading(false);
			}
		}
	};

	const renderedResults =
		results.length !== 0 || groupText?.length < 3 || loading ? (
			results.map((group) => (
				<TypeaheadItem key={group.id} value={group.id}>
					<React.Fragment>
						<strong>{group.displayName}</strong> ({group.id})
					</React.Fragment>
				</TypeaheadItem>
			))
		) : (
			<TypeaheadItem value={'0'}>
				<React.Fragment aria-disabled>
					<i>No results found.</i>
				</React.Fragment>
			</TypeaheadItem>
		);

	return !props.disabled ? (
		<Typeahead
			label={props.label}
			note={nonValidMessage || props.note}
			optionalClass={!isValid ? 'em-has-error' : ''}
			onChange={handleTextChange}
			value={groupText}
			placeholder={props.placeholder}
			clickHandler={(value) => {
				const obj = results.find((o) => o.id === value);
				props.onChange(obj);
				setGroupText(obj?.displayName ?? '');
			}}
		>
			{loading ? <LoadingIndicator size="s" /> : renderedResults}
		</Typeahead>
	) : (
		<ReadOnlyField label={props.label} text={groupText} />
	);
};

export default GroupLookup;
